import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import './TestReport.css';

const TestReport = () => {
    const { t } = useTranslation(['reports', 'common', 'test', 'loader']);
    const location = useLocation();
    const navigate = useNavigate();

    const state = location.state || {};

    const rawQuestions = state.questions;
    const rawAnswers = state.answers;
    const questions = Array.isArray(rawQuestions) && rawQuestions.length > 0 ? rawQuestions : null;
    const answers = rawAnswers || {};
    const subject = state.subject || 'Science';
    const cls = state.class || '10';
    const chapter = state.chapter || 'Chapter 1';
    const board = state.board || 'CBSE';

    const questionList = useMemo(() => {
        if (questions) return questions;
        return [{
            id: 1, question: "No questions loaded.", options: { A: "-", B: "-", C: "-", D: "-" },
            correct_answer: "A", explanation: "", difficulty: "easy"
        }];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questions]); // Added questions to dependency array for questionList

    const { correct, incorrect, skipped, scorePercent } = useMemo(() => {
        let correct = 0, incorrect = 0, skipped = 0;
        questionList.forEach((q, i) => {
            const userAns = answers[i];
            if (!userAns) { skipped++; }
            else if (userAns === q.correct_answer) { correct++; }
            else { incorrect++; }
        });
        const total = questions.length;
        return {
            correct,
            incorrect,
            skipped,
            scorePercent: total > 0 ? Math.round((correct / total) * 100) : 0
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questionList, answers]);

    const circumference = 2 * Math.PI * 54;
    const strokeOffset = circumference - (scorePercent / 100) * circumference;

    const getGradeInfo = (pct) => {
        if (pct >= 90) return { grade: 'A+', label: t('reports:reports.testReport.labels.excellent', 'Excellent!'), color: '#00ff96' };
        if (pct >= 75) return { grade: 'A', label: t('reports:reports.testReport.labels.great', 'Great Work!'), color: '#3b82f6' };
        if (pct >= 60) return { grade: 'B', label: t('reports:reports.testReport.labels.good', 'Good Job!'), color: '#f59e0b' };
        if (pct >= 40) return { grade: 'C', label: t('reports:reports.testReport.labels.keepGoing', 'Keep Going!'), color: '#f97316' };
        return { grade: 'D', label: t('reports:reports.testReport.labels.needPractice', 'Need Practice'), color: '#ef4444' };
    };

    const { grade, label, color } = getGradeInfo(scorePercent);

    const getOptionStyle = (q, optId, i) => {
        const userAns = answers[i];
        const isCorrect = optId === q.correct_answer;
        const isSelected = optId === userAns;
        if (isCorrect) return 'tr-opt correct';
        if (isSelected && !isCorrect) return 'tr-opt wrong';
        return 'tr-opt';
    };

    const getQuestionStatus = (q, i) => {
        const userAns = answers[i];
        if (!userAns) return { icon: '—', cls: 'status-skip', label: t('reports:reports.testReport.skipped', 'Skipped') };
        if (userAns === q.correct_answer) return { icon: '✓', cls: 'status-correct', label: t('reports:reports.testReport.correct', 'Correct') };
        return { icon: '✗', cls: 'status-wrong', label: t('reports:reports.testReport.wrong', 'Wrong') };
    };


    return (
        <div className="tr-container">
            {/* Background glows */}
            <div className="tr-bg">
                <div className="tr-glow-1"></div>
                <div className="tr-glow-2"></div>
            </div>

            {/* Header */}
            <header className="tr-header">
                <div className="tr-header-left">
                    <span className="tr-badge">📊 {t('reports:reports.testReport.title', 'Test Report')}</span>

                    <div>
                        <h1 className="tr-title">{subject} · {t('loader:loader.classLabel', { cls, defaultValue: `Class ${cls}` })}</h1>
                        <p className="tr-subtitle">{board} &nbsp;·&nbsp; {chapter}</p>
                    </div>

                </div>
                <div className="tr-header-actions">
                    <button className="tr-btn-outline" onClick={() => navigate('/dashboard')}>
                        🏠 {t('reports:reports.testReport.dashboard', 'Dashboard')}
                    </button>
                    <button className="tr-btn-primary" onClick={() => navigate('/ai-doctor-report', { state })}>
                        🩺 {t('reports:reports.testReport.deepAnalysis', 'Deep AI Analysis')}
                    </button>
                    <button className="tr-btn-primary" onClick={() => navigate('/loader', { state: { board, class: cls, subject, chapter } })}>
                        🔄 {t('reports:reports.testReport.retake', 'Retake Test')}
                    </button>
                </div>

            </header>

            {/* Score Summary */}
            <section className="tr-summary">
                {/* Circular Score Gauge */}
                <div className="tr-score-card">
                    <div className="tr-gauge-wrap">
                        <svg viewBox="0 0 120 120" width="160" height="160">
                            <circle cx="60" cy="60" r="54" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="10" />
                            <circle
                                cx="60" cy="60" r="54"
                                fill="none"
                                stroke="url(#trGrad)"
                                strokeWidth="10"
                                strokeLinecap="round"
                                strokeDasharray={circumference}
                                strokeDashoffset={strokeOffset}
                                transform="rotate(-90 60 60)"
                                style={{ transition: 'stroke-dashoffset 1s ease' }}
                            />
                            <defs>
                                <linearGradient id="trGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#00f2ff" />
                                    <stop offset="100%" stopColor="#8b5cf6" />
                                </linearGradient>
                            </defs>
                        </svg>
                        <div className="tr-gauge-center">
                            <span className="tr-score-pct" style={{ color }}>{scorePercent}%</span>
                            <span className="tr-grade" style={{ color }}>{grade}</span>
                        </div>
                    </div>
                    <p className="tr-result-label" style={{ color }}>{label}</p>
                </div>

                {/* Stats */}
                <div className="tr-stats">
                    <div className="tr-stat-card tr-stat-correct">
                        <span className="tr-stat-icon">✓</span>
                        <span className="tr-stat-num">{correct}</span>
                        <span className="tr-stat-lbl">{t('reports:reports.testReport.correct', 'Correct')}</span>
                    </div>

                    <div className="tr-stat-card tr-stat-wrong">
                        <span className="tr-stat-icon">✗</span>
                        <span className="tr-stat-num">{incorrect}</span>
                        <span className="tr-stat-lbl">{t('reports:reports.testReport.wrong', 'Wrong')}</span>
                    </div>

                    <div className="tr-stat-card tr-stat-skip">
                        <span className="tr-stat-icon">—</span>
                        <span className="tr-stat-num">{skipped}</span>
                        <span className="tr-stat-lbl">{t('reports:reports.testReport.skipped', 'Skipped')}</span>
                    </div>

                    <div className="tr-stat-card tr-stat-total">
                        <span className="tr-stat-icon">📝</span>
                        <span className="tr-stat-num">{questionList.length}</span>
                        <span className="tr-stat-lbl">{t('reports:reports.testReport.total', 'Total')}</span>
                    </div>
                </div>

            </section>

            {/* Per-Question Review */}
            <section className="tr-review">
                <h2 className="tr-review-title">{t('reports:reports.testReport.reviewTitle', 'Question Review')}</h2>
                <div className="tr-questions-list">

                    {questionList.map((q, i) => {
                        const status = getQuestionStatus(q, i);
                        const optionEntries = Array.isArray(q.options)
                            ? q.options.map(o => [o.id, o.text])
                            : Object.entries(q.options);
                        return (
                            <div className={`tr-qcard ${status.cls}`} key={i}>
                                <div className="tr-qcard-header">
                                    <span className="tr-qnum">Q{i + 1}</span>
                                    <span className={`tr-qstatus ${status.cls}`}>
                                        {status.icon} {status.label}
                                    </span>
                                    {q.difficulty && (
                                        <span className={`tr-difficulty tr-diff-${q.difficulty}`}>
                                            {t(`test:test.difficulty.${q.difficulty}`, q.difficulty)}
                                        </span>
                                    )}

                                </div>

                                <p className="tr-qtext">{q.question}</p>

                                <div className="tr-options">
                                    {optionEntries.map(([optId, optText]) => (
                                        <div key={optId} className={getOptionStyle(q, optId, i)}>
                                            <span className="tr-opt-label">{optId}</span>
                                            <span className="tr-opt-text">{optText}</span>
                                            {optId === q.correct_answer && (
                                                <span className="tr-tick">✓ {t('reports:reports.testReport.correct', 'Correct')}</span>
                                            )}

                                            {optId === answers[i] && optId !== q.correct_answer && (
                                                <span className="tr-cross">✗ {t('reports:reports.testReport.yourAnswer', 'Your Answer')}</span>
                                            )}

                                        </div>
                                    ))}
                                </div>

                                {q.explanation && (
                                    <div className="tr-explanation">
                                        <span className="tr-expl-icon">💡</span>
                                        <span>{q.explanation}</span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
};

export default TestReport;
